/**
 * A typed page in Pages (`project/:p/page/item/:ref`): the shared typed page
 * view -- crumb, title, the single-valued field row, the body, Links -- as on
 * the desktop. The body is the item's `tracker-content/<id>` room through the
 * console's editor mount, told which typed page it is so its links offer
 * relations; fields and title write through the tracker room.
 *
 * `ref` is an issue key or an item id, as page links write it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePageModule, type PageModule } from './pageModule';
import { useOutletContext, useParams } from 'react-router-dom';
import { asTeamDocumentId, asTeamProjectId } from '@nimbalyst/collab-bundle/editor';
import { useCollabDocsUI, useCollabPagesState, useSharedFolders } from '@nimbalyst/collab-bundle/docs-ui';
import {
  TrackerSurfaceMessage,
  useTrackerCommand,
  useTrackerDataSelector,
  type TeamMemberOption,
} from '@nimbalyst/collab-bundle/trackers-ui';
import { CollabEditorMount } from '../collab/CollabEditorMount';
import { useDocsShell } from '../collab/CollabDocsShell';
import { WebCollabHost } from '../collab/WebCollabHost';
import { memberDisplayName } from '../collab/orgRoster';
import { CollabLoadingPanel } from '../components/Chrome';
import { consoleConfig } from '../config';
import { resolveTrackerItemRef } from '../links/consoleLinkLanding';
import type { OrganizationRouteContext } from '../routes/workspaceContext';
import { orgPageItemPath } from '../routing';
import { useOptionalProjectTrackerSession } from '../trackers/ProjectTrackerSession';
import { usePagesTrackers } from './PagesTrackers';
import { createConsolePageLinksSource } from './webPageLinksSource';
import { pagesPlacedViewTypes } from './placedViewTypes';
import { usePageTitleDraft } from './usePageTitleDraft';
import { isEmbedded } from '../embedded/embedMarker';

export function WebTypedPageRoute() {
  const { itemRef } = useParams<{ itemRef: string }>();
  const { available } = usePagesTrackers();
  const module = usePageModule();
  if (!available || !module || !itemRef) {
    return <CollabLoadingPanel title="Opening this page" detail="Connecting to your team's pages." />;
  }
  return <TypedPage itemRef={itemRef} module={module} />;
}

function TypedPage({ itemRef, module }: { itemRef: string; module: PageModule }) {
  const { team, projectId, navigate } = useDocsShell();
  const { teamSession, getTeamJwt } = useOutletContext<OrganizationRouteContext>();
  const projectTrackers = useOptionalProjectTrackerSession();
  const recordsById = useTrackerDataSelector((state) => state.recordsById);
  const records = useTrackerDataSelector((state) => state.records);
  const loaded = useTrackerDataSelector((state) => state.loaded);
  const command = useTrackerCommand();
  const { host } = useCollabDocsUI();
  const { documents, typePlacements, itemPlacements } = useCollabPagesState();
  const folders = useSharedFolders();
  const itemId = resolveTrackerItemRef(itemRef, recordsById);
  const item = itemId ? recordsById.get(itemId) ?? null : null;

  const crumb = useMemo(
    () => (item
      ? module.trackerPageCrumb(item.id, item.primaryType, { itemPlacements, typePlacements, documents, folders, item: module.crumbItemLookup(recordsById) })
      : { ancestors: [], underType: false }),
    [module, item, itemPlacements, typePlacements, documents, folders, recordsById],
  );

  const storedTitle = typeof item?.fields.title === 'string' ? item.fields.title : '';
  const [mutationError, setMutationError] = useState<string | null>(null);
  const update = useCallback(async (updates: Record<string, unknown>) => {
    if (!item) throw new Error('This page is unavailable');
    const outcome = await command({ type: 'update-item', input: { itemId: item.id, updates, sharing: 'team' } });
    const result = outcome.result as { success?: boolean; error?: string } | undefined;
    if (result?.success === false) throw new Error(result.error || 'The page could not be saved');
  }, [command, item?.id]);
  const titleDraft = usePageTitleDraft(`${teamSession.orgId}/${projectId}/${itemId ?? itemRef}`, storedTitle,
    title => update({ title }));
  const archive = useCallback(() => {
    if (!item) return;
    setMutationError(null);
    void command({ type: 'archive-item', itemId: item.id, archive: true }).then(outcome => {
      const result = outcome.result as { success?: boolean; error?: string } | undefined;
      if (result?.success === false) throw new Error(result.error || 'The page could not be archived');
    }).catch((error: unknown) => setMutationError(error instanceof Error ? error.message : String(error)));
  }, [command, item?.id]);

  const recordsRef = useRef(records);
  recordsRef.current = records;
  const bodyLinks = useMemo(() => {
    if (!(host instanceof WebCollabHost)) return null;
    return host.pageLinksSource((ref: string) => {
      const id = resolveTrackerItemRef(ref, recordsById);
      const record = id ? recordsById.get(id) : null;
      return record ? { id: record.id, issueKey: record.issueKey ?? null, typeId: record.primaryType, title: String(record.fields.title ?? '') } : null;
    });
  }, [host, recordsById]);
  const linksSource = useMemo(() => createConsolePageLinksSource({
    fieldLinks: (id) => module.fieldRelationLinks(id, recordsRef.current),
    bodyLinks,
  }), [module, bodyLinks]);
  // Re-read Links when the room's records change (a relation picked in the body
  // or a field edit lands there) and when the server says body links changed.
  const [linksRevision, setLinksRevision] = useState(0);
  useEffect(() => { setLinksRevision((value) => value + 1); }, [records]);
  useEffect(() => bodyLinks?.subscribe(() => setLinksRevision((value) => value + 1)), [bodyLinks]);

  const teamMembers = useMemo<TeamMemberOption[]>(
    () => (projectTrackers?.members ?? []).map((member) => ({ email: member.email, name: memberDisplayName(member) })),
    [projectTrackers?.members],
  );
  const source = useMemo(() => (item ? {
    kind: 'team-room' as const,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    room: { orgId: teamSession.orgId, projectId: asTeamProjectId(projectId), documentId: asTeamDocumentId(`tracker-content/${item.id}`) },
    auth: { scope: 'team' as const, memberId: teamSession.memberId, getTeamJwt },
  } : null), [item?.id, teamSession.orgId, teamSession.memberId, projectId, getTeamJwt]);
  // The editor's presence label falls back from name to email to "Teammate".
  const selfEmail = projectTrackers?.members.find((member) => member.memberId === teamSession.memberId)?.email;
  const user = useMemo(
    () => ({ memberId: teamSession.memberId, name: projectTrackers?.presenceName, email: selfEmail }),
    [teamSession.memberId, projectTrackers?.presenceName, selfEmail],
  );
  const referenceSource = useMemo(() => (item ? { itemId: item.id, type: item.primaryType } : undefined), [item?.id, item?.primaryType]);

  useEffect(() => {
    if (!item) return;
    const previous = document.title;
    document.title = `${storedTitle || 'Untitled'} · Nimbalyst`;
    return () => { document.title = previous; };
  }, [item, storedTitle]);

  if (!item && loaded) {
    return <TrackerSurfaceMessage icon="search_off" message="This page is not in this project." hint="It may have been deleted, or it may belong to another project." testId="typed-page-missing" />;
  }
  const { TrackerPageView } = module;
  const embedded = isEmbedded();
  return (
    <div className="web-typed-page h-full min-h-0" data-testid="web-typed-page">
      {titleDraft.status !== 'saved' ? <div className="px-4 py-2 text-xs" role={titleDraft.error ? 'alert' : 'status'}>
        {titleDraft.error ? <>Title not saved: {titleDraft.error}. Your draft is kept in this tab. <button type="button" className="text-nim-link" onClick={titleDraft.retry}>Retry</button></> : 'Saving title…'}
      </div> : null}
      {mutationError ? <div className="px-4 py-2 text-xs text-nim-error" role="alert">{mutationError}</div> : null}
      <TrackerPageView
        item={item}
        loaded={loaded}
        crumb={crumb}
        // The phone app reads typed pages; properties and body stay read-only there in v1.
        editable={!embedded}
        title={titleDraft.title}
        onRename={titleDraft.rename}
        fieldValues={item?.fields ?? {}}
        onUpdateField={(field, value) => { setMutationError(null); void update({ [field.name]: value }).catch(error => setMutationError(error.message)); }}
        teamMembers={teamMembers}
        renderBody={() => (item && source ? (
          <CollabEditorMount
            key={item.id}
            source={source}
            user={user}
            readOnly={embedded ? true : undefined}
            trackerReferences={projectTrackers?.references ?? null}
            trackerReferenceAppearance="quiet"
            trackerReferenceSource={referenceSource}
            placedViewTypes={pagesPlacedViewTypes}
          />
        ) : null)}
        linksSource={linksSource}
        linksRevision={linksRevision}
        onOpenItem={(id) => navigate(orgPageItemPath(team, projectId, recordsById.get(id)?.issueKey ?? id))}
        onArchive={archive}
      />
    </div>
  );
}
